/**
 * Entity Registry — maps entity type keys to their services, observables, and column definitions.
 * Adding a new entity (e.g., timesheets, time cards) is a single-entry addition.
 */
import { Injector, Type } from '@angular/core';
import { Observable } from 'rxjs';

import { ReportEntityType } from '../models/saved-report.model';

// Entity services
import { ContractService } from '../../service/entity-services/contract.service';
import { PartyService } from '../../service/entity-services/party.service';
import { PeopleService } from '../../service/entity-services/people.service';
import { ProvisionsService } from '../../service/entity-services/provisions.service';
import { BaaService } from '../../service/entity-services/baa.service';
import { CoiService } from '../../service/entity-services/coi.service';
import { AssessmentsService } from '../../service/entity-services/assessments.service';
import { CriticalDatesService } from '../../service/entity-services/critical-dates.service';
import { SiteService } from '../../service/entity-services/site.service';
import { LegalEntityService } from '../../service/entity-services/legal-entity.service';
import { UsersService } from '../../service/entity-services/users.service';

// Column definitions
import * as contractColumns from '../entity-columns/contract-columns';
import * as partiesColumns from '../entity-columns/parties-columns';
import * as peopleColumns from '../entity-columns/people-columns';
import * as provisionsColumns from '../entity-columns/provisions-columns';
import * as baaColumns from '../entity-columns/baa-columns';
import * as coiColumns from '../entity-columns/coi-columns';
import * as assessmentColumns from '../entity-columns/assessment-columns';
import * as importantDatesColumns from '../entity-columns/important-dates-columns';
import * as sitesColumns from '../entity-columns/sites-columns';
import * as legalEntityColumns from '../entity-columns/legal-entity-columns';
import * as usersColumns from '../entity-columns/users-columns';


export interface EntityRegistryEntry {
  key: ReportEntityType;
  label: string;
  icon: string;
  serviceType: Type<any>;
  /** Property name on the service that returns Observable<any[]> of all entity data */
  observableName: string;
  /** All available column definitions for this entity */
  columns: any[];
  /** Column IDs to show by default when starting a fresh report */
  defaultColumnIds: string[];
  /** Optional setup function called when this entity is selected (e.g., injector setup for contract columns) */
  setup?: (injector: Injector) => void;
}


export const ENTITY_REGISTRY: Record<ReportEntityType, EntityRegistryEntry> = {

  contracts: {
    key: 'contracts',
    label: 'Contracts',
    icon: 'description',
    serviceType: ContractService,
    observableName: 'visibleContracts$',
    columns: contractColumns.allColumns,
    defaultColumnIds: [
      'contractId', 'contractStatus', 'nameOfContract',
      'contractType', 'party', 'expirationDate', 'renewalType'
    ],
    setup: (injector: Injector) => {
      contractColumns.setContractColumnsInjector(injector);
    }
  },

  parties: {
    key: 'parties',
    label: 'Parties / Vendors',
    icon: 'business',
    serviceType: PartyService,
    observableName: 'allParties$',
    columns: partiesColumns.allColumns,
    defaultColumnIds: [
      'vendorNum', 'vendorName', 'partyType', 'city', 'state', 'standing'
    ]
  },

  people: {
    key: 'people',
    label: 'People',
    icon: 'people',
    serviceType: PeopleService,
    observableName: 'allPeople$',
    columns: peopleColumns.allColumns,
    defaultColumnIds: [
      'firstName', 'lastName', 'email', 'primaryVendor'
    ]
  },

  provisions: {
    key: 'provisions',
    label: 'Provisions',
    icon: 'gavel',
    serviceType: ProvisionsService,
    observableName: 'allProvisions$',
    columns: provisionsColumns.allColumns,
    defaultColumnIds: [
      'provNum', 'provName', 'sourceType', 'riskDomain', 'likelihoodScore', 'impactScore'
    ]
  },

  baas: {
    key: 'baas',
    label: 'BAAs',
    icon: 'security',
    serviceType: BaaService,
    observableName: 'allBaas$',
    columns: baaColumns.allColumns,
    defaultColumnIds: [
      'baaNum', 'businessAssociateName', 'executionDate', 'expirationDate'
    ]
  },

  cois: {
    key: 'cois',
    label: 'Certificates of Insurance',
    icon: 'verified_user',
    serviceType: CoiService,
    observableName: 'allCois$',
    columns: coiColumns.allColumns,
    defaultColumnIds: [
      'coiNum', 'insuranceProvider', 'coverageType', 'expirationDate'
    ]
  },

  assessments: {
    key: 'assessments',
    label: 'Assessments',
    icon: 'assessment',
    serviceType: AssessmentsService,
    observableName: 'allAssessments$',
    columns: assessmentColumns.allColumns,
    defaultColumnIds: [
      'assessmentName', 'assessmentType', 'status', 'createdDate'
    ]
  },

  'critical-dates': {
    key: 'critical-dates',
    label: 'Critical Dates',
    icon: 'event',
    serviceType: CriticalDatesService,
    observableName: 'visibleCriticalDates$',
    columns: importantDatesColumns.allColumns,
    defaultColumnIds: [
      'dateLabel', 'date', 'contractName', 'owner'
    ]
  },

  sites: {
    key: 'sites',
    label: 'Sites',
    icon: 'location_on',
    serviceType: SiteService,
    observableName: 'allSites$',
    columns: sitesColumns.allColumns,
    defaultColumnIds: [
      'siteName', 'address', 'city', 'state'
    ]
  },

  'legal-entities': {
    key: 'legal-entities',
    label: 'Legal Entities',
    icon: 'account_balance',
    serviceType: LegalEntityService,
    observableName: 'allLegalEntities$',
    columns: legalEntityColumns.allColumns,
    defaultColumnIds: [
      'legalEntityName', 'legalEntityType', 'state'
    ]
  },

  users: {
    key: 'users',
    label: 'Users',
    icon: 'manage_accounts',
    serviceType: UsersService,
    observableName: 'allUsers$',
    columns: usersColumns.allColumns,
    defaultColumnIds: [
      // These must be real colIds from users-columns.ts — 'friendlyName' was not
      // one (the Full Name column's colId is 'userFullName'), so it silently
      // never rendered.
      'userFullName', 'email', 'role', 'lastLogin'
    ]
  }
};

/**
 * Get sorted list of entity registry entries for display in dropdowns.
 */
export function getEntityRegistryList(): EntityRegistryEntry[] {
  return Object.values(ENTITY_REGISTRY).sort((a, b) => a.label.localeCompare(b.label));
}
